import React, { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { key: 1, label: '24h' },
  { key: 7, label: '7d' },
  { key: 30, label: '30d' },
];

export default function AdsCard() {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [trend, setTrend] = useState([]);
  const [adsEnabled, setAdsEnabled] = useState(false);
  const [publisherId, setPublisherId] = useState('');

  useEffect(() => {
    axios.get(`${API}/settings/ads`).then(r => {
      setAdsEnabled(!!r.data.ads_enabled);
      setPublisherId(r.data.ads_publisher_id || '');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/analytics/summary?days=${days}`)
      .then(r => {
        const summary = r.data || {};
        const evts = summary.events || {};
        setTotal(evts.ad_impression?.total || 0);
        // Trend endpoint returns total events per bucket; approximate by scaling proportionally
        const bucketTotal = (summary.trend || []).reduce((s, b) => s + b.count, 0) || 1;
        const rate = (evts.ad_impression?.total || 0) / bucketTotal;
        setTrend((summary.trend || []).map(b => ({
          bucket: b.bucket,
          impressions: Math.round(b.count * rate),
        })));
      })
      .catch(() => { setTotal(0); setTrend([]); })
      .finally(() => setLoading(false));
  }, [days]);

  const max = Math.max(1, ...trend.map(b => b.impressions));

  const adsenseHost = 'https://www.google.com/adsense/new/u/0/';
  const adsenseUrl = publisherId ? `${adsenseHost}${publisherId.replace('ca-pub-', 'pub-')}/main/viewreports` : adsenseHost;

  return (
    <div data-testid="ads-card" className="bg-[#171A21] rounded-2xl border border-white/5 p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <DollarSign size={18} className="text-[#D4AF37]" />
          <h2 className="text-lg font-semibold text-white">Ad Performance</h2>
        </div>
        <div className="flex bg-white/5 rounded-full p-1">
          {RANGES.map(r => (
            <button
              key={r.key}
              data-testid={`ads-range-${r.key}`}
              onClick={() => setDays(r.key)}
              className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
                days === r.key ? 'bg-[#D4AF37] text-[#0F1115]' : 'text-[#94A3B8] hover:text-white'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!adsEnabled ? (
        <p data-testid="ads-off-note" className="text-[#94A3B8] text-sm">
          Ads are currently off. Enable them in <a href="/admin/settings" className="text-[#D4AF37] hover:underline">Settings</a> once your AdSense site is approved.
        </p>
      ) : loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="text-[#D4AF37] animate-spin" />
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-semibold text-white" data-testid="ads-impressions-total">{total.toLocaleString()}</p>
            <p className="text-[#94A3B8] text-xs">tracked impressions · consented users only</p>
          </div>

          {trend.length > 0 && (
            <div className="mt-5 flex items-end gap-1 h-16" data-testid="ads-trend">
              {trend.map((b, i) => {
                const h = Math.max(4, Math.round((b.impressions / max) * 100));
                return (
                  <div
                    key={i}
                    className="flex-1 bg-[#D4AF37]/25 rounded-t hover:bg-[#D4AF37]/60 transition-colors"
                    style={{ height: `${h}%` }}
                    title={`${b.bucket}: ${b.impressions}`}
                  />
                );
              })}
            </div>
          )}

          <div className="mt-5 flex items-center justify-between text-xs text-[#94A3B8] gap-2 flex-wrap">
            <span className="flex items-center gap-1"><TrendingUp size={12} /> First-party tracking (users on "Accept all")</span>
            <a
              data-testid="ads-open-adsense"
              href={adsenseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline"
            >
              Full earnings in AdSense <ExternalLink size={11} />
            </a>
          </div>
        </>
      )}
    </div>
  );
}
